// Contabilità del servizio fiduciario, con la stessa struttura di TabContabilita:
// stat card, rettifiche al compenso, movimenti, preventivi/proforma collegati e
// generazione proforma tramite utils/generaProformaPdf.ts.
// Specifico dei servizi:
// - ore pianificate = ricavo cliente; ore pagate (override o GPS) = costo collaboratore;
// - i movimenti INCASSO/RIMBORSO sono mostrati come entrata/uscita.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, Calendar, User as UserIcon, MessageSquare,
    ChevronDown, ChevronRight, Pencil, Check, X, Trash2, PlusCircle,
    Wallet, FileDown, AlertTriangle, ShieldCheck, FileText, Download, Receipt,
    Tag, ArrowDownRight, ArrowUpRight, Landmark,
} from 'lucide-react';
import {
    RelazioneGiornalieraService, ServizioFiduciarioService,
    PreventiviService, ClientService, SettingsService,
} from '../services/apiService';
import { useToast } from './ToastContext';
import { parseDecimal, localIsoDate } from '../utils/utils';
import { oreFra } from '../utils/oreFra';
import { generaProformaPdf, calcolaTotaliProforma, ProformaRow as PdfProformaRow } from '../utils/generaProformaPdf';
import NumberInput from './NumberInput';
import {
    calcolaCanali, IvaMode, IVA_MODE_LABEL, IVA_MODE_SHORT, DEFAULT_RETTIFICA_MODE, DEFAULT_INCASSO_MODE, splitRettifica, splitIncasso, parseIvaMode,
} from '../utils/contabilitaCanali';
import { economiaProforma, hashMarkerBaseIva, rigaRettificaProforma, ivaExtraDaCanali } from '../utils/proformaPayload';

interface Props {
    servizioId: number;
    nomeServizio: string;
    nomeCliente: string;
    clientId?: number | string | null;
    tariffaOrariaCliente: number;
    // Prezzo cliente: 'ORARIA' (ore × tariffa) o 'FORFAIT' (prezzo fisso mensile da contratto).
    tipoTariffaCliente?: 'ORARIA' | 'FORFAIT' | string;
    prezzoForfaitCliente?: number;
    tariffeCollaboratori: any[];
    turni: any[];
    onTurniRefresh?: () => void;
    datiAggiuntiviJson?: string | null;
    onDatiAggiuntiviChange?: (json: string | null) => void;
}

// Il tipo interno (INCASSO/ANTICIPO/RIMBORSO/ALTRO) resta compatibile con Amministrazione.
// In UI: INCASSO/ANTICIPO/ALTRO → entrata, RIMBORSO → uscita.
interface Movimento {
    id: string;
    data: string;
    descrizione: string;
    tipo: 'INCASSO' | 'RIMBORSO' | 'ANTICIPO' | 'ALTRO';
    importo: number;
    fatturato?: boolean;
    // Come l'incasso incide sui canali imponibile/IVA.
    ivaMode?: IvaMode;
}

interface Aggiustamento {
    id: string;
    descrizione: string;
    importo: number;
    ivaMode?: IvaMode;
}

interface ProformaRowLocal {
    id: string;
    descrizione: string;
    quantita: string;
    tariffa: string;
    importo: number;
    isExtra?: boolean;
    noIva?: boolean;   // rettifica "Solo imponibile": nel totale ma esclusa dalla base IVA
    inTotal?: boolean; // false = riga informativa fuori dal totale (rettifica "Solo IVA")
}

// oreFra sta in utils/oreFra.ts, così il test non carica l'intero componente.
const oreFraDate = (start?: string, end?: string): number => {
    if (!start || !end) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e) || e <= s) return 0;
    return Math.round(((e - s) / 3_600_000) * 100) / 100;
};
const fmtEur = (n: number) => `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const parseJsonBlob = (json?: string | null): any => {
    if (!json) return {};
    try { return JSON.parse(json) || {}; } catch { return {}; }
};
const parseMovimenti = (json?: string | null): Movimento[] => {
    const obj = parseJsonBlob(json);
    return Array.isArray(obj.movimenti_contabili) ? obj.movimenti_contabili : [];
};
const parseAggiustamenti = (json?: string | null): Aggiustamento[] => {
    const obj = parseJsonBlob(json);
    return Array.isArray(obj.aggiustamenti) ? obj.aggiustamenti : [];
};
const isEntrata = (m: Movimento) => m.tipo === 'INCASSO' || m.tipo === 'ANTICIPO' || m.tipo === 'ALTRO';
const segnoImporto = (m: Movimento) => isEntrata(m) ? Number(m.importo) || 0 : -(Number(m.importo) || 0);

export const TabContabilitaServizio: React.FC<Props> = ({
    servizioId, nomeServizio, nomeCliente, clientId, tariffaOrariaCliente,
    tipoTariffaCliente, prezzoForfaitCliente,
    tariffeCollaboratori, turni, onTurniRefresh,
    datiAggiuntiviJson, onDatiAggiuntiviChange,
}) => {
    const isForfait = tipoTariffaCliente === 'FORFAIT';
    const forfaitAmount = Number(prezzoForfaitCliente) || 0;
    const { addToast } = useToast();
    const navigate = useNavigate();
    // Periodo di default: dal primo all'ultimo turno assegnato, senza limitarsi a oggi
    // perché i turni futuri vanno mostrati. Si applica quando arrivano i turni (prop asincrona).
    const todayIso = useCallback(() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }, []);
    const [dataDal, setDataDal] = useState<string>(todayIso());
    const [dataAl, setDataAl] = useState<string>(todayIso());
    const [periodoInited, setPeriodoInited] = useState(false);
    // Il default si applica una sola volta: se l'utente cambia le date non le sovrascriviamo.
    useEffect(() => {
        if (periodoInited) return;
        if (!Array.isArray(turni) || turni.length === 0) return;
        const dates = turni
            .map((t: any) => t?.dataTurno)
            .filter((d: any) => typeof d === 'string' && d.length >= 10)
            .map((d: string) => d.slice(0, 10))
            .sort();
        if (dates.length > 0) {
            setDataDal(dates[0]);
            setDataAl(dates[dates.length - 1]);
        }
        setPeriodoInited(true);
    }, [turni, periodoInited]);

    const [reports, setReports] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Aliquota IVA fissa al 22%, come in TabContabilita.
    const aliquotaIva = 22;
    const [settings, setSettings] = useState<any>(null);

    // Note operatore
    const [expandedTurnoId, setExpandedTurnoId] = useState<number | null>(null);
    const [noteByTurno, setNoteByTurno] = useState<Record<number, any[]>>({});
    const [loadingNote, setLoadingNote] = useState<number | null>(null);

    // Override e approvazione ore si gestiscono nella tab Report: qui sono in sola lettura.

    // Movimenti e rettifiche (salvati nel JSON)
    const [movimenti, setMovimenti] = useState<Movimento[]>(() => parseMovimenti(datiAggiuntiviJson));
    const [aggiustamenti, setAggiustamenti] = useState<Aggiustamento[]>(() => parseAggiustamenti(datiAggiuntiviJson));

    // Modale Rettifiche
    const [aggModalOpen, setAggModalOpen] = useState(false);
    const [aggEdit, setAggEdit] = useState<Aggiustamento[]>([]);
    const [savingAgg, setSavingAgg] = useState(false);

    // Nuovo movimento: solo incassi, come nei mandati.
    // Rimborsi ed extra negativi passano dalle rettifiche al compenso.
    const [movModalOpen, setMovModalOpen] = useState(false);
    const [newMov, setNewMov] = useState<{ data: string; descrizione: string; importo: string; ivaMode: IvaMode }>({
        data: localIsoDate(),
        descrizione: '', importo: '', ivaMode: DEFAULT_INCASSO_MODE,
    });
    const [savingMov, setSavingMov] = useState(false);

    // Generazione proforma
    const [proformaModalOpen, setProformaModalOpen] = useState(false);
    const [proformaRows, setProformaRows] = useState<ProformaRowLocal[]>([]);
    const [newRowDraft, setNewRowDraft] = useState<{ descrizione: string; quantita: string; tariffa: string; importo: string }>({ descrizione: '', quantita: '', tariffa: '', importo: '' });
    const [generatingProforma, setGeneratingProforma] = useState(false);

    // Preventivi/Proforma emesse
    const [preventiviCollegati, setPreventiviCollegati] = useState<any[]>([]);
    const [loadingPrev, setLoadingPrev] = useState(false);

    useEffect(() => {
        setMovimenti(parseMovimenti(datiAggiuntiviJson));
        setAggiustamenti(parseAggiustamenti(datiAggiuntiviJson));
    }, [datiAggiuntiviJson]);

    // Impostazioni agenzia per il PDF
    useEffect(() => {
        let cancelled = false;
        SettingsService.getAll().then((res: any) => {
            const list = res?.data || (Array.isArray(res) ? res : []);
            const s = Array.isArray(list) && list.length > 0 ? list[0] : null;
            if (cancelled) return;
            setSettings(s);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const tariffaCollabMap = useMemo(() => {
        const m: Record<number, number> = {};
        (tariffeCollaboratori || []).forEach(t => {
            if (t.collaboratore?.id != null) m[t.collaboratore.id] = Number(t.tariffaOraria) || 0;
        });
        return m;
    }, [tariffeCollaboratori]);

    // Rimborso km del collaboratore: personale (User.rimborsoKm) o da Impostazioni,
    // con la stessa priorità usata nei mandati e in /amministrazione.
    const rimborsoKmCollabMap = useMemo(() => {
        const m: Record<number, number> = {};
        const fallback = Number(settings?.priceKM) || 0;
        (tariffeCollaboratori || []).forEach(t => {
            const cid = t.collaboratore?.id;
            if (cid != null) {
                const perCollab = Number(t.collaboratore?.rimborsoKm);
                m[cid] = Number.isFinite(perCollab) && perCollab > 0 ? perCollab : fallback;
            }
        });
        return m;
    }, [tariffeCollaboratori, settings]);

    const loadReports = useCallback(async () => {
        setLoading(true);
        try {
            const res = await RelazioneGiornalieraService.getByServizio(servizioId, { dataDal, dataAl });
            const data = Array.isArray(res) ? res : (res?.data || []);
            setReports(data);
        } catch {
            addToast('Errore caricamento consuntivo', 'error');
        } finally {
            setLoading(false);
        }
    }, [servizioId, dataDal, dataAl, addToast]);

    useEffect(() => {
        loadReports();
        // Al ritorno del focus ricarica i dati, così le approvazioni fatte altrove
        // si riflettono subito sul consuntivo.
        const onFocus = () => loadReports();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [loadReports]);

    // Preventivi dello stesso cliente non collegati a mandati o servizi ("Pendenti del cliente").
    const [preventiviPendenti, setPreventiviPendenti] = useState<any[]>([]);
    const fetchPreventivi = useCallback(async () => {
        setLoadingPrev(true);
        try {
            const res = await PreventiviService.getByServizio(servizioId);
            setPreventiviCollegati(((res as any)?.data) || []);
        } catch {} finally { setLoadingPrev(false); }
        if (clientId) {
            try {
                const res = await PreventiviService.getPendingByClient(clientId);
                setPreventiviPendenti(((res as any)?.data) || []);
            } catch { /* widget facoltativo */ }
        } else {
            setPreventiviPendenti([]);
        }
    }, [servizioId, clientId]);
    useEffect(() => { fetchPreventivi(); }, [fetchPreventivi]);

    // Collega un preventivo libero al servizio; il BE verifica che il cliente coincida.
    const linkPreventivo = async (prevId: number | string) => {
        try {
            await PreventiviService.linkToServizio(prevId, Number(servizioId));
            await fetchPreventivi();
            addToast('Preventivo collegato al servizio', 'success');
        } catch (e: any) {
            addToast('Errore nel collegamento: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };
    // Scollega un preventivo/proforma: il documento torna libero, per questo chiede conferma.
    const unlinkPreventivo = async (prevId: number | string) => {
        if (!window.confirm('Scollegare questo documento dal servizio? Tornerà disponibile per altri collegamenti.')) return;
        try {
            await PreventiviService.linkToServizio(prevId, null);
            await fetchPreventivi();
            addToast('Documento scollegato', 'success');
        } catch (e: any) {
            addToast('Errore nello scollegamento: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };

    // Elimina una proforma emessa per il servizio, previa conferma.
    const deleteProforma = async (prevId: number | string) => {
        if (!window.confirm('Eliminare definitivamente questa proforma? Il PDF resterà comunque archiviato su S3 ma non sarà più visibile qui.')) return;
        try {
            await PreventiviService.delete(prevId);
            await fetchPreventivi();
            addToast('Proforma eliminata', 'success');
        } catch (e: any) {
            addToast('Errore eliminazione: ' + (e?.message || 'sconosciuto'), 'error');
        }
    };

    const reportByTurno = useMemo(() => {
        const m: Record<number, any> = {};
        reports.forEach(r => { if (r.turnoId != null) m[r.turnoId] = r; });
        return m;
    }, [reports]);

    const turniDelMese = useMemo(() => {
        // Ordine cronologico (a parità di giorno per oraInizio), filtrato sul periodo inclusivo.
        return (turni || [])
            .filter(t => t.dataTurno && t.dataTurno >= dataDal && t.dataTurno <= dataAl)
            .filter(t => t.statoAccettazione !== 'RIFIUTATO' && !t.dataRevoca)
            .slice() // copia: evita di mutare la prop in input
            .sort((a, b) => {
                const cmpData = String(a.dataTurno).localeCompare(String(b.dataTurno));
                if (cmpData !== 0) return cmpData;
                return String(a.oraInizio || '').localeCompare(String(b.oraInizio || ''));
            });
    }, [turni, dataDal, dataAl]);

    // Etichetta "gg/mm/aaaa – gg/mm/aaaa" per le descrizioni della proforma.
    const periodoLabel = useMemo(() => {
        const fmt = (iso: string): string => {
            if (!iso || iso.length < 10) return iso || '';
            const [y, m, d] = iso.slice(0, 10).split('-');
            return `${d}/${m}/${y}`;
        };
        return `${fmt(dataDal)} – ${fmt(dataAl)}`;
    }, [dataDal, dataAl]);

    const righe = useMemo(() => {
        return turniDelMese.map((t: any) => {
            const orePianificate = oreFra(t.oraInizio, t.oraFine);
            const oreGps = oreFraDate(t.effettivoInizio, t.effettivoFine);
            const rep = reportByTurno[t.id];
            // Ore extra approvate: lavorate fuori GPS e dichiarate nel report. Contano per il
            // costo del collaboratore, non per il ricavo (il cliente paga le ore pianificate).
            const oreExtraApprovate = Number(rep?.oreExtraApprovate) || 0;
            // Ore pagate: override del titolare se presente, altrimenti GPS + extra.
            const orePagate = t.oreCollaboratoreOverride != null
                ? Number(t.oreCollaboratoreOverride)
                : oreGps + oreExtraApprovate;
            const tariffaCollab = tariffaCollabMap[t.collaboratore?.id] ?? 0;
            // Km rimborsati al collaboratore solo con relazione APPROVATO, come per le spese
            // e in /amministrazione. Con auto aziendale sono 0.
            const isApproved = rep?.stato === 'APPROVATO';
            const kmDaRimborsare = isApproved && !rep.autoAziendale
                ? Number(rep.kmApprovati ?? 0)
                : 0;
            const tariffaRimborsoKm = rimborsoKmCollabMap[t.collaboratore?.id] ?? 0;
            const rimborsoKm = kmDaRimborsare * tariffaRimborsoKm;
            const costoCollab = orePagate * tariffaCollab + rimborsoKm;
            // Ricavo cliente = ore pianificate × tariffa (le extra riguardano solo il collaboratore).
            // A forfait il ricavo si applica sul totale (vedi totali), quindi la riga vale 0.
            const ricavoCliente = isForfait
                ? 0
                : orePianificate * (tariffaOrariaCliente || 0);
            // Spese a rivalsa: solo scontrini approvati, non anticipati dall'azienda e non buoni pasto
            // (benefit al collaboratore). I km non si rivendono al cliente nei servizi.
            // Non entrano da sole nella base: il titolare le aggiunge come rettifica.
            const speseApprovate = (rep?.speseEspanso || []).reduce((acc: number, s: any) => {
                if (s.anticipatoAzienda) return acc;
                if (s.buonoPastoAuto) return acc;
                const importo = Number(s.importoApprovato) || 0;
                return importo > 0 ? acc + importo : acc;
            }, 0);
            // Stato del turno: pianificato (senza GPS), svolto (GPS chiuso) o in corso.
            const isSvolto = !!t.effettivoInizio && !!t.effettivoFine;
            const isInCorso = !!t.effettivoInizio && !t.effettivoFine;
            const isAccettato = t.statoAccettazione === 'ACCETTATO';
            return {
                turno: t, orePianificate, oreGps, orePagate,
                oreExtraApprovate,
                oreApprovate: !!t.oreApprovate,
                isSvolto, isInCorso, isAccettato,
                tariffaCollab, costoCollab, ricavoCliente, speseApprovate,
                kmDaRimborsare, tariffaRimborsoKm, rimborsoKm,
                rep, statoReport: rep?.stato || null,
            };
        });
    }, [turniDelMese, tariffaCollabMap, rimborsoKmCollabMap, tariffaOrariaCliente, reportByTurno, isForfait]);

    const totali = useMemo(() => {
        const sommati = righe.reduce((acc, r) => ({
            orePianificate: acc.orePianificate + r.orePianificate,
            oreExtraApprovate: acc.oreExtraApprovate + r.oreExtraApprovate,
            orePagate: acc.orePagate + r.orePagate,
            costoCollab: acc.costoCollab + r.costoCollab,
            ricavoCliente: acc.ricavoCliente + r.ricavoCliente,
            spese: acc.spese + r.speseApprovate,
            kmDaRimborsare: acc.kmDaRimborsare + r.kmDaRimborsare,
            rimborsoKm: acc.rimborsoKm + r.rimborsoKm,
        }), { orePianificate: 0, oreExtraApprovate: 0, orePagate: 0, costoCollab: 0, ricavoCliente: 0, spese: 0, kmDaRimborsare: 0, rimborsoKm: 0 });
        // A forfait il ricavo mensile è il prezzo di contratto e sostituisce la somma per turno.
        if (isForfait) sommati.ricavoCliente = forfaitAmount;
        return sommati;
    }, [righe, isForfait, forfaitAmount]);

    // Serve approvare le ore solo dei turni svolti. Il banner è informativo: la proforma
    // si basa sulle ore pianificate, non sul costo del collaboratore.
    const turniSvoltiNonApprovati = useMemo(
        () => righe.filter(r => r.isSvolto && !r.oreApprovate).length,
        [righe]
    );
    const turniSvolti = useMemo(() => righe.filter(r => r.isSvolto).length, [righe]);
    const tuttoApprovato = turniSvolti > 0 && turniSvoltiNonApprovati === 0;

    const sumAggiustamenti = useMemo(() => aggiustamenti.reduce((s, a) => s + (Number(a.importo) || 0), 0), [aggiustamenti]);
    const hasAggiustamenti = aggiustamenti.length > 0;

    // Base = solo ricavo cliente (ore × tariffa o forfait). Le spese a rivalsa si aggiungono
    // come rettifica, come nei mandati; i km nei servizi non si fatturano.
    const baseConsuntivo = totali.ricavoCliente;
    // Canali imponibile/IVA indipendenti. I tipi dei movimenti servizio vengono
    // convertiti in entrata/uscita per calcolaCanali.
    const canali = useMemo(() => calcolaCanali({
        imponibileBase: baseConsuntivo,
        aggiustamenti,
        movimenti: movimenti.map(m => ({
            importo: Number(m.importo) || 0,
            tipo: isEntrata(m) ? 'entrata' : 'uscita',
            fatturato: m.fatturato,
            ivaMode: m.ivaMode,
        })),
    }), [baseConsuntivo, aggiustamenti, movimenti]);
    const compensoFinale = canali.imponibileDovuto;
    const ivaValore = canali.ivaDovuta;
    const totaleIvato = canali.totaleDovuto;
    const totaleIncassato = canali.totaleIncassato;
    // Quota delle rettifiche sul canale imponibile (base + questa = imponibile).
    const sumAggImponibile = Math.round((canali.imponibileDovuto - baseConsuntivo) * 100) / 100;
    const sumAggIva = Math.round((canali.ivaDovuta - canali.ivaBase) * 100) / 100;

    // Ordine cronologico crescente; i movimenti senza data usano "9999" per finire in fondo.
    const movimentiOrdinati = useMemo(() => {
        return movimenti.slice().sort((a, b) => {
            const da = a.data && a.data.length > 0 ? a.data : '9999-12-31';
            const db = b.data && b.data.length > 0 ? b.data : '9999-12-31';
            return da.localeCompare(db);
        });
    }, [movimenti]);

    // ─── Note operatore ──────────────────────────────────────────────────────
    const toggleExpand = async (turnoId: number) => {
        if (expandedTurnoId === turnoId) { setExpandedTurnoId(null); return; }
        setExpandedTurnoId(turnoId);
        if (noteByTurno[turnoId] !== undefined) return;
        setLoadingNote(turnoId);
        try {
            const res = await ServizioFiduciarioService.getNoteTurno(turnoId);
            const list = (res as any)?.data ?? (Array.isArray(res) ? res : []);
            setNoteByTurno(prev => ({ ...prev, [turnoId]: Array.isArray(list) ? list : [] }));
        } catch {
            setNoteByTurno(prev => ({ ...prev, [turnoId]: [] }));
        } finally {
            setLoadingNote(null);
        }
    };

    // Override e approvazione ore sono nella tab Report.

    // ─── Persistenza JSON ───────────────────────────────────────────────────
    const persistJson = async (next: { movimenti?: Movimento[]; aggiustamenti?: Aggiustamento[] }) => {
        if (!onDatiAggiuntiviChange) return;
        const obj = parseJsonBlob(datiAggiuntiviJson);
        if (next.movimenti !== undefined) obj.movimenti_contabili = next.movimenti;
        if (next.aggiustamenti !== undefined) obj.aggiustamenti = next.aggiustamenti;
        const json = JSON.stringify(obj);
        await ServizioFiduciarioService.patchDatiAggiuntivi(servizioId, json);
        onDatiAggiuntiviChange(json);
    };

    // ─── Rettifiche al compenso ─────────────────────────────────────────────
    const openAggModal = () => {
        setAggEdit(aggiustamenti.map(a => ({ ...a })));
        setAggModalOpen(true);
    };
    const handleSaveAgg = async () => {
        setSavingAgg(true);
        try {
            const next = aggEdit.filter(a => a.descrizione.trim() || Number(a.importo) !== 0);
            await persistJson({ aggiustamenti: next });
            setAggiustamenti(next);
            setAggModalOpen(false);
            addToast('Rettifiche salvate', 'success');
        } catch { addToast('Errore salvataggio rettifiche', 'error'); }
        finally { setSavingAgg(false); }
    };

    // Come nei mandati: le spese approvate del periodo diventano una rettifica con id
    // sentinella, così includerle è una scelta esplicita del titolare. Idempotente.
    const SPESE_RIVALSA_VOCE_ID = 'auto-spese-rivalsa';
    const [addingSpeseToRettifiche, setAddingSpeseToRettifiche] = useState(false);
    const aggiungiSpeseAlleRettifiche = async () => {
        const totaleSpese = totali.spese;
        if (totaleSpese <= 0) {
            addToast('Nessuna spesa a rivalsa da aggiungere', 'error');
            return;
        }
        const existing = aggiustamenti.find(a => a.id === SPESE_RIVALSA_VOCE_ID);
        const ok = window.confirm(
            existing
                ? `Aggiornare la rettifica "Spese a rivalsa" col nuovo totale (€ ${totaleSpese.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})})?\nSarà inclusa nel prossimo proforma generato.`
                : `Aggiungere una rettifica "Spese a rivalsa" (€ ${totaleSpese.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}) al compenso?\nSarà inclusa nel prossimo proforma generato.`
        );
        if (!ok) return;
        setAddingSpeseToRettifiche(true);
        try {
            // Stesso filtro di speseApprovate: niente buoni pasto né spese anticipate dall'azienda.
            const tipi = Array.from(new Set(reports
                .filter((r: any) => r.stato === 'APPROVATO')
                .flatMap((r: any) => (r.speseEspanso || [])
                    .filter((s: any) => !s.buonoPastoAuto && !s.anticipatoAzienda)
                    .map((s: any) => s.tipo || ''))
                .filter(Boolean)));
            const descrSuffix = tipi.length > 0 ? ` (${tipi.join(', ').toLowerCase()})` : '';
            const nuovaRettifica: Aggiustamento = {
                id: SPESE_RIVALSA_VOCE_ID,
                descrizione: `Spese a rivalsa${descrSuffix}`,
                importo: Number(totaleSpese.toFixed(2)),
                // Nei servizi le spese a rivalsa sono sempre escluse da IVA.
                ivaMode: 'SOLO_IMPONIBILE',
            };
            const idx = aggiustamenti.findIndex(a => a.id === SPESE_RIVALSA_VOCE_ID);
            const next = [...aggiustamenti];
            if (idx >= 0) next[idx] = nuovaRettifica; else next.push(nuovaRettifica);
            await persistJson({ aggiustamenti: next });
            setAggiustamenti(next);
            addToast(idx >= 0 ? 'Rettifica spese aggiornata' : 'Rettifica spese aggiunta', 'success');
        } catch {
            addToast('Errore aggiornamento rettifiche', 'error');
        } finally {
            setAddingSpeseToRettifiche(false);
        }
    };

    // ─── Movimenti ──────────────────────────────────────────────────────────
    const openNuovoMov = () => {
        setNewMov({ data: localIsoDate(), descrizione: '', importo: '', ivaMode: DEFAULT_INCASSO_MODE });
        setMovModalOpen(true);
    };
    const handleSaveNuovoMov = async () => {
        const importo = parseDecimal(newMov.importo);
        if (!newMov.descrizione.trim()) { addToast('Inserisci una descrizione', 'error'); return; }
        if (importo <= 0) { addToast('Inserisci un importo > 0', 'error'); return; }
        const mov: Movimento = {
            id: `mov-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            data: newMov.data,
            descrizione: newMov.descrizione.trim(),
            tipo: 'INCASSO',
            importo,
            fatturato: false,
            ivaMode: newMov.ivaMode,
        };
        const next = [...movimenti, mov];
        setSavingMov(true);
        try {
            await persistJson({ movimenti: next });
            setMovimenti(next);
            setMovModalOpen(false);
        } catch { addToast('Errore salvataggio movimento', 'error'); }
        finally { setSavingMov(false); }
    };
    const eliminaMovimento = async (id: string) => {
        if (!window.confirm('Eliminare questo movimento?')) return;
        const next = movimenti.filter(m => m.id !== id);
        try { await persistJson({ movimenti: next }); setMovimenti(next); }
        catch { addToast('Errore eliminazione movimento', 'error'); }
    };
    const toggleFatturato = async (id: string) => {
        const next = movimenti.map(m => m.id === id ? { ...m, fatturato: !m.fatturato } : m);
        try { await persistJson({ movimenti: next }); setMovimenti(next); }
        catch { addToast('Errore aggiornamento movimento', 'error'); }
    };

    // ─── Proforma ─────────────────────────────────────────────────────────────
    const apriProformaModal = () => {
        if (!clientId) { addToast('Manca il cliente del servizio: salvalo prima', 'error'); return; }
        if (righe.length === 0) { addToast('Nessun turno nel periodo — niente da fatturare', 'error'); return; }
        // Nessun blocco sulle ore non approvate: la proforma usa le ore pianificate.
        // Si mostra solo un avviso se ci sono ore svolte da approvare.
        if (turniSvoltiNonApprovati > 0) {
            addToast(`Attenzione: ${turniSvoltiNonApprovati} turni svolti non hanno ancora le ore approvate (il costo collab nel proforma è indicativo)`, 'info');
        }
        const rows: ProformaRowLocal[] = [];
        if (isForfait) {
            // Forfait: una sola riga col prezzo di contratto, uguale ogni mese.
            rows.push({
                id: 'r-servizio',
                descrizione: `Forfait contratto · ${nomeServizio} · ${periodoLabel}`,
                quantita: '—',
                tariffa: '—',
                importo: forfaitAmount,
            });
        } else {
            // Ore cliente = pianificate × tariffa. Le ore extra del collaboratore non vanno in proforma.
            rows.push({
                id: 'r-servizio',
                descrizione: `Servizio fiduciario · ${nomeServizio} · ${periodoLabel}`,
                quantita: `${totali.orePianificate.toFixed(2)}h`,
                tariffa: `${fmtEur(tariffaOrariaCliente)}/h`,
                importo: totali.ricavoCliente,
            });
        }
        // Le spese a rivalsa entrano solo se il titolare le ha convertite in rettifica
        // ('auto-spese-rivalsa'), tramite il ciclo sulle rettifiche qui sotto.
        // Modalità IVA come nella proforma del mandato: IVA inclusa → solo la parte imponibile,
        // Solo IVA → riga informativa fuori dal totale (IVA in ivaExtra).
        aggiustamenti.forEach(a => {
            const mode = parseIvaMode(a.ivaMode, DEFAULT_RETTIFICA_MODE);
            const riga = rigaRettificaProforma(a.importo, mode);
            rows.push({
                id: `r-agg-${a.id}`,
                descrizione: mode === 'SOLO_IVA' ? `${a.descrizione || 'Rettifica'} (solo IVA)` : (a.descrizione || 'Rettifica'),
                quantita: '—', tariffa: '—', importo: riga.importo,
                ...(riga.noIva ? { noIva: true } : {}),
                ...(riga.inTotal ? {} : { inTotal: false }),
            });
        });
        setProformaRows(rows);
        setProformaModalOpen(true);
    };
    const addProformaRow = () => {
        const importoNum = parseDecimal(newRowDraft.importo);
        if (!newRowDraft.descrizione.trim() && importoNum === 0) {
            addToast('Inserisci almeno descrizione o importo', 'error');
            return;
        }
        setProformaRows([...proformaRows, {
            id: `r-extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            descrizione: newRowDraft.descrizione.trim() || 'Voce aggiuntiva',
            quantita: newRowDraft.quantita.trim() || '—',
            tariffa: newRowDraft.tariffa.trim() || '—',
            importo: importoNum, isExtra: true,
        }]);
        setNewRowDraft({ descrizione: '', quantita: '', tariffa: '', importo: '' });
    };
    const removeProformaRow = (id: string) => setProformaRows(proformaRows.filter(r => r.id !== id));
    const updateProformaRowImporto = (id: string, value: string) => {
        const importo = parseDecimal(value);
        setProformaRows(proformaRows.map(r => r.id === id ? { ...r, importo } : r));
    };
    // Righe noIva ("Solo imponibile"): nel totale ma fuori dalla base IVA, come nel PDF.
    const proformaRowsPdf: PdfProformaRow[] = proformaRows.map(r => ({
        id: r.id, descrizione: r.descrizione, quantita: r.quantita, tariffa: r.tariffa,
        importo: Number(r.importo) || 0, inTotal: r.inTotal !== false, isExtra: r.isExtra,
        ...(r.noIva ? { noIva: true } : {}),
    }));
    // IVA extra allineata alla card (come nel mandato) solo con rettifiche IVA inclusa o
    // Solo IVA: senza, il calcolo resta quello di prima (IVA sulle righe).
    const haRettificheIvaSpeciali = aggiustamenti.some(a => {
        const m = parseIvaMode(a.ivaMode, DEFAULT_RETTIFICA_MODE);
        return m === 'LORDO' || m === 'SOLO_IVA';
    });
    const proformaEsclusiRighe = proformaRowsPdf.reduce((s, r) => s + (r.inTotal && r.noIva ? r.importo : 0), 0);
    const ivaExtraProforma = haRettificheIvaSpeciali
        ? ivaExtraDaCanali(canali.ivaDovuta, canali.imponibileDovuto, proformaEsclusiRighe)
        : 0;
    const proformaTotali = calcolaTotaliProforma(proformaRowsPdf, aliquotaIva, false, ivaExtraProforma);
    const proformaImponibile = proformaTotali.totaleBase;
    const proformaIva = proformaTotali.iva;
    const proformaTotale = proformaTotali.totale;

    // Hash del contenuto (righe + IVA): la proforma servizio non mostra pagamenti, quindi
    // gli incassi non contano. Il BE riusa la proforma con lo stesso hash invece di
    // assegnare un nuovo numero a ogni download.
    const computeProformaHash = (): string => {
        const sig = JSON.stringify({
            v: 1,
            rows: proformaRows.map((r: any) => ({
                d: (r.descrizione || '').trim(),
                q: (r.quantita || '').trim(),
                t: (r.tariffa || '').trim(),
                i: Number(r.importo) || 0,
                tot: r.inTotal !== false,
                sec: false,
                // noIva solo se true, così l'hash delle proforme senza righe esenti non cambia.
                ...(r.noIva ? { ni: 1 } : {}),
            })),
            mode: 'completo',
            nascondiIva: false,
            mostraPagamenti: false,
            iva: Number(aliquotaIva) || 0,
            incassi: 0,
            acconto: 0,
            ivaExtra: Number(ivaExtraProforma) || 0,
            ...hashMarkerBaseIva(economiaProforma(proformaRowsPdf, ivaExtraProforma)),
        });
        let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
        for (let i = 0; i < sig.length; i++) {
            const ch = sig.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
    };

    const handleGeneraProforma = async () => {
        if (proformaRows.length === 0) { addToast('Nessuna riga nel proforma', 'error'); return; }
        if (!clientId) { addToast('Manca il cliente del servizio', 'error'); return; }
        setGeneratingProforma(true);
        try {
            const clientRes = await ClientService.getById(clientId).catch(() => null);
            const cliente: any = (clientRes as any)?.data || clientRes || {};

            // 1. Crea prima il record PROFORMA: il BE assegna il numero (PF-YYYY-NNNN) usato
            // nel PDF, così PDF e lista coincidono. Le voci vanno convertite in numeri per l'editor.
            const vociPreventivo = proformaRows.map(r => ({
                id: r.id,
                descrizione: r.descrizione.trim() + (r.quantita && r.quantita !== '—' && r.quantita.trim() !== '' ? ` (${r.quantita.trim()}${r.tariffa && r.tariffa !== '—' ? ' × ' + r.tariffa.trim() : ''})` : ''),
                quantita: 1,
                unita: 'forfait',
                prezzoUnitario: Number(r.importo) || 0,
                scontoPercentuale: 0,
            }));
            let created: any = null;
            try {
                const createRes = await PreventiviService.create({
                    clientMode: 'existing', clientId: Number(clientId),
                    servizioFiduciarioId: servizioId,
                    voci: vociPreventivo,
                    // Quota esente e IVA extra al BE: il record ha lo stesso totale del PDF.
                    ...economiaProforma(proformaRowsPdf, ivaExtraProforma), ivaPercentuale: aliquotaIva,
                    stato: 'INVIATO', tipoDocumento: 'PROFORMA',
                    proformaContentHash: computeProformaHash(),
                    notePerCliente: `Periodo: ${periodoLabel}`,
                    noteInterne: `Generato da TabContabilitaServizio · servizio ${servizioId}`,
                });
                created = (createRes as any)?.data ?? null;
            } catch (e) {
                console.warn('[handleGeneraProforma] create Preventivo fallito, procedo con fallback runtime:', e);
            }

            // 2. Numero del documento: quello del BE, oppure un fallback per non bloccare il titolare.
            const oggi = new Date();
            const docNumber = created?.numeroProgressivo
                ?? `PF-${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}${String(oggi.getDate()).padStart(2, '0')}-S${servizioId}`;
            const oggetto: string[] = [`Servizio fiduciario: ${nomeServizio}`, `Periodo: ${periodoLabel}`];
            const pagamenti = movimenti.filter(isEntrata).map(m => ({
                data: m.data, descrizione: m.descrizione || 'Pagamento ricevuto', importo: Number(m.importo) || 0,
            }));
            const rowsForPdf = proformaRowsPdf;
            const safeName = nomeServizio.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
            const result = await generaProformaPdf({
                rows: rowsForPdf, mode: 'completo',
                settings: settings || {}, cliente,
                docNumber, oggetto, safeName,
                aliquotaIva, totaleIncassi: totaleIncassato, pagamenti,
                ivaExtra: ivaExtraProforma,
            });
            if (!result) { addToast('Errore generazione PDF', 'error'); return; }
            const url = URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = url; a.download = result.filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito

            // 3. Carica il PDF sul record creato; se la creazione era fallita non c'è nulla da archiviare.
            if (created?.reused) {
                // Contenuto identico: la proforma esiste già con PDF su S3, niente nuovo upload.
                setPreventiviCollegati(prev => [created, ...prev.filter((x: any) => x.id !== created.id)]);
                addToast(`Proforma ${created.numeroProgressivo || ''} riscaricata (contenuto invariato)`.trim(), 'success');
            } else if (created?.id) {
                try {
                    const uploadRes = await PreventiviService.savePdf(created.id, result.blob, result.filename);
                    const updated = (uploadRes as any)?.data ?? created;
                    setPreventiviCollegati(prev => [updated, ...prev.filter((x: any) => x.id !== updated.id)]);
                    addToast('Proforma generata e archiviata sul servizio', 'success');
                } catch {
                    addToast('PDF scaricato ma non archiviato sul servizio', 'error');
                }
            } else {
                addToast('PDF scaricato ma non archiviato sul servizio', 'error');
            }
            setProformaModalOpen(false);
        } catch (e) {
            console.error('[handleGeneraProforma]', e);
            addToast('Errore generazione proforma', 'error');
        } finally {
            setGeneratingProforma(false);
        }
    };

    const proformaList = preventiviCollegati.filter((p: any) => p.tipoDocumento === 'PROFORMA');
    const preventivoList = preventiviCollegati.filter((p: any) => p.tipoDocumento !== 'PROFORMA');

    return (
        <div className="space-y-6">
            {/* Intestazione: periodo e Genera Proforma */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Calendar size={16} className="text-slate-400"/>
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                        Dal
                        <input type="date" value={dataDal}
                               max={dataAl}
                               onChange={e => { setDataDal(e.target.value); setPeriodoInited(true); }}
                               className="p-2 border border-slate-200 rounded-lg text-sm" />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                        Al
                        <input type="date" value={dataAl}
                               min={dataDal}
                               onChange={e => { setDataAl(e.target.value); setPeriodoInited(true); }}
                               className="p-2 border border-slate-200 rounded-lg text-sm" />
                    </label>
                </div>
                <button onClick={apriProformaModal} disabled={!clientId || righe.length === 0}
                        className="text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed"
                        title={!clientId ? 'Manca il cliente' : righe.length === 0 ? 'Nessun turno nel periodo' : 'Genera Proforma'}>
                    <FileDown size={14}/> Genera Proforma
                </button>
            </div>

            {/* Stat card: totale ivato / totale incassato */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-between">
                    <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Totale Compenso</span>
                        <div className="p-2 bg-slate-100 text-slate-600 rounded-lg"><Wallet size={16}/></div>
                    </div>
                    <div>
                        {/* Numero grande = imponibile; sotto totale e IVA. */}
                        <div className="text-2xl font-black text-slate-900">{fmtEur(compensoFinale)}</div>
                        <div className="text-xs font-semibold text-slate-500 mt-1">
                            Totale {fmtEur(totaleIvato)} · IVA {aliquotaIva}% {fmtEur(ivaValore)}
                        </div>
                    </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-2xl shadow-sm flex flex-col justify-between">
                    <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest">Totale Incassato</span>
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg"><Landmark size={16}/></div>
                    </div>
                    <div>
                        {/* Numero grande = imponibile incassato; sotto totale e IVA. */}
                        <div className="text-2xl font-black text-emerald-700">{fmtEur(canali.imponibileIncassato)}</div>
                        {totaleIncassato > 0 && (
                            <div className="text-[11px] font-bold text-emerald-700/70 mt-0.5">
                                Totale {fmtEur(totaleIncassato)} · IVA {aliquotaIva}% {fmtEur(canali.ivaIncassata)}
                            </div>
                        )}
                        <div className="text-xs font-semibold text-emerald-600/70 mt-1">
                            {canali.isSaldato ? 'Saldato ✓' : `Da versare: imp ${fmtEur(Math.max(0, canali.residuoImponibile))} · IVA ${fmtEur(Math.max(0, canali.residuoIva))}`}
                        </div>
                    </div>
                </div>
            </div>

            {/* Spese a rivalsa: il titolare decide se aggiungerle come rettifica.
               Per i km non c'è pulsante, nei servizi non hanno tariffa di vendita. */}
            {totali.spese > 0 && (() => {
                const speseInRettifiche = aggiustamenti.find(a => a.id === SPESE_RIVALSA_VOCE_ID);
                const giaSync = speseInRettifiche && Math.abs(Number(speseInRettifiche.importo) - totali.spese) < 0.005;
                return (
                    <div className="bg-white border border-amber-200 rounded-2xl shadow-sm p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="p-2 bg-amber-100 text-amber-600 rounded-lg shrink-0"><Receipt size={16}/></div>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-amber-800">Spese a rivalsa</div>
                                    <div className="text-[11px] text-slate-500">
                                        {fmtEur(totali.spese)} · scontrini approvati nel periodo
                                        {speseInRettifiche && (
                                            <span className={`ml-2 font-semibold ${giaSync ? 'text-emerald-600' : 'text-amber-700'}`}>
                                                {giaSync ? '· già in rettifiche ✓' : `· in rettifiche: ${fmtEur(Number(speseInRettifiche.importo))} (da aggiornare)`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={aggiungiSpeseAlleRettifiche}
                                disabled={addingSpeseToRettifiche || giaSync}
                                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg shadow-sm transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                                title={giaSync ? 'Già in rettifiche con questo importo' : 'Aggiungi/aggiorna queste spese come rettifica al compenso (finirà nel prossimo proforma)'}
                            >
                                {addingSpeseToRettifiche ? <Loader2 size={12} className="animate-spin"/> : <PlusCircle size={12}/>}
                                {speseInRettifiche ? 'Aggiorna rettifica' : 'Aggiungi alle rettifiche'}
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Rettifiche al compenso */}
            <div className={`bg-white border rounded-2xl shadow-sm p-4 ${hasAggiustamenti ? 'border-blue-200' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${hasAggiustamenti ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}><Tag size={16}/></div>
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800">Rettifiche al Compenso</div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {hasAggiustamenti
                                    ? `${aggiustamenti.length} voce${aggiustamenti.length > 1 ? 'i' : ''} · imp. ${sumAggImponibile >= 0 ? '+' : '−'} €${Math.abs(sumAggImponibile).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · IVA ${sumAggIva >= 0 ? '+' : '−'} €${Math.abs(sumAggIva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                    : 'Aggiunte o detrazioni sul compenso base (extra, sconti, ecc.)'}
                            </div>
                        </div>
                    </div>
                    <button onClick={openAggModal}
                            className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 border transition-colors ${
                                hasAggiustamenti
                                    ? 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-100'
                                    : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                            }`}>
                        <Pencil size={12}/> {hasAggiustamenti ? 'Modifica' : 'Aggiungi rettifica'}
                    </button>
                </div>
                {hasAggiustamenti && (
                    <div className="mt-3 space-y-1">
                        <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                            <span className="text-xs text-slate-500">Base (consuntivo servizio)</span>
                            <span className="text-xs font-bold text-slate-700">{fmtEur(baseConsuntivo)}</span>
                        </div>
                        {aggiustamenti.map(a => {
                            const mode = (a.ivaMode as IvaMode) || DEFAULT_RETTIFICA_MODE;
                            const raw = Number(a.importo) || 0;
                            const neg = raw < 0;
                            const sp = splitRettifica(raw, mode);
                            return (
                            <div key={a.id} className="flex items-center justify-between gap-2 py-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded shrink-0 ${neg ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                        {neg ? '−' : '+'}
                                    </span>
                                    <span className="text-xs text-slate-600 truncate">{a.descrizione || 'Rettifica'}</span>
                                    <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1 py-0.5 rounded shrink-0">{IVA_MODE_SHORT[mode]}</span>
                                </div>
                                <div className="text-right shrink-0">
                                    <span className={`text-xs font-bold ${neg ? 'text-rose-600' : 'text-emerald-600'}`}>{neg ? '−' : '+'} € {Math.abs(raw).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                    <div className="text-[9px] text-slate-400">imp {sp.imp >= 0 ? '+' : '−'}€{Math.abs(sp.imp).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · IVA {sp.iva >= 0 ? '+' : '−'}€{Math.abs(sp.iva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                            </div>
                            );
                        })}
                        <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
                            <span className="text-xs font-bold text-slate-700">Totale rettificato (imponibile)</span>
                            <span className="text-sm font-black text-blue-700">{fmtEur(compensoFinale)}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Avviso non bloccante: ore svolte non ancora approvate */}
            {!loading && turniSvolti > 0 && (
                <div className={`bg-white border rounded-2xl shadow-sm p-4 flex items-center justify-between gap-3 ${
                    tuttoApprovato ? 'border-emerald-200' : 'border-amber-200'
                }`}>
                    <div className="flex items-center gap-3 text-sm">
                        <div className={`p-2 rounded-lg ${tuttoApprovato ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                            {tuttoApprovato ? <ShieldCheck size={16}/> : <AlertTriangle size={16}/>}
                        </div>
                        <span className={tuttoApprovato ? 'text-emerald-800 font-medium' : 'text-amber-800 font-medium'}>
                            {tuttoApprovato
                                ? `Ore approvate per tutti i ${turniSvolti} turni svolti.`
                                : `${turniSvoltiNonApprovati} turni svolti con ore da approvare per il costo collaboratore.`}
                        </span>
                    </div>
                </div>
            )}

            {/* Tabella turni (consuntivo operativo) */}
            <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Consuntivo Operativo</h3>
                {loading ? (
                    <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center text-slate-400 flex items-center justify-center gap-2">
                        <Loader2 className="animate-spin" size={18}/> Caricamento consuntivo...
                    </div>
                ) : righe.length === 0 ? (
                    <div className="bg-white p-12 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400 text-sm">
                        Nessun turno nel periodo selezionato.
                    </div>
                ) : (
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="w-8 p-3"></th>
                                    <th className="p-3">Data</th>
                                    <th className="p-3">Collaboratore</th>
                                    <th className="p-3 text-right" title="Ore fatturate al cliente = pianificate + ore extra approvate">Ore Cliente</th>
                                    <th className="p-3 text-right" title="Ore GPS effettive">Ore GPS</th>
                                    <th className="p-3 text-right" title="Ore pagate al collaboratore">Ore Pagate</th>
                                    <th className="p-3 text-right">Ricavo</th>
                                    <th className="p-3 text-right">Costo</th>
                                    <th className="p-3 text-right">Spese</th>
                                    <th className="p-3 text-center">Stato</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm">
                                {righe.map(r => {
                                    const isExpanded = expandedTurnoId === r.turno.id;
                                    const note = noteByTurno[r.turno.id] || [];
                                    return (
                                        <React.Fragment key={r.turno.id}>
                                            <tr className="hover:bg-slate-50 transition-colors">
                                                <td className="p-3 text-center">
                                                    <button onClick={() => toggleExpand(r.turno.id)} className="text-slate-400 hover:text-indigo-600"
                                                            title={isExpanded ? 'Chiudi note' : 'Mostra note operatore'}>
                                                        {isExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                                                    </button>
                                                </td>
                                                <td className="p-3 font-semibold text-slate-800">
                                                    {new Date(r.turno.dataTurno).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                                                    <div className="text-[10px] text-slate-400 font-normal">{r.turno.oraInizio} - {r.turno.oraFine}</div>
                                                </td>
                                                <td className="p-3 text-slate-700">
                                                    <div className="flex items-center gap-1.5">
                                                        <UserIcon size={12} className="text-slate-400"/>
                                                        {r.turno.collaboratore?.name} {r.turno.collaboratore?.surname || ''}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-right font-mono font-bold text-slate-900">{r.orePianificate.toFixed(2)}h</td>
                                                <td className="p-3 text-right font-mono text-slate-400 text-xs">
                                                    {r.oreGps > 0 ? `${r.oreGps.toFixed(2)}h` : '—'}
                                                </td>
                                                <td className="p-3 text-right">
                                                    {/* Sola lettura: si modifica dalla tab Report. */}
                                                    <div className="flex items-center justify-end gap-1 font-mono text-xs text-slate-600">
                                                        <span>{r.orePagate.toFixed(2)}h</span>
                                                    </div>
                                                    {r.oreExtraApprovate > 0 && r.turno.oreCollaboratoreOverride == null && (
                                                        <div className="text-[10px] text-slate-400 font-mono">
                                                            {r.oreGps.toFixed(2)} GPS + {r.oreExtraApprovate.toFixed(2)} extra
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right font-bold text-slate-900">{isForfait ? '—' : fmtEur(r.ricavoCliente)}</td>
                                                <td className="p-3 text-right font-mono text-slate-500 text-xs">
                                                    <div>{fmtEur(r.costoCollab)}</div>
                                                    {/* Dettaglio rimborso km, solo se il collaboratore ha usato l'auto propria. */}
                                                    {r.rimborsoKm > 0 && (
                                                        <div className="text-[10px] text-slate-400 font-mono">
                                                            + {r.kmDaRimborsare} km × {fmtEur(r.tariffaRimborsoKm)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-right font-mono text-slate-600">{r.speseApprovate > 0 ? fmtEur(r.speseApprovate) : '—'}</td>
                                                <td className="p-3 text-center">
                                                    {/* Sola lettura: l'approvazione ore si fa dalla tab Report. */}
                                                    {r.isSvolto ? (
                                                        r.oreApprovate ? (
                                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded"
                                                                  title="Ore svolte e approvate (gestione in tab Report)">
                                                                <ShieldCheck size={10}/> APPROVATE
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded"
                                                                  title="Turno svolto, ore non ancora approvate (approvazione in tab Report)">
                                                                <AlertTriangle size={10}/> DA APPROVARE
                                                            </span>
                                                        )
                                                    ) : r.isInCorso ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded animate-pulse" title="Il collaboratore è in servizio adesso">
                                                            IN SERVIZIO
                                                        </span>
                                                    ) : r.isAccettato ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded" title="Turno pianificato, non ancora svolto">
                                                            DA ESPLETARE
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-slate-50 text-slate-500 border border-slate-200 px-2 py-1 rounded" title="Turno assegnato, il collaboratore deve ancora accettare">
                                                            DA ACCETTARE
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-slate-50/60">
                                                    <td colSpan={10} className="px-12 py-4">
                                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1">
                                                            <MessageSquare size={12}/> Note operatore durante il turno
                                                        </div>
                                                        {loadingNote === r.turno.id ? (
                                                            <div className="text-slate-400 text-sm flex items-center gap-2">
                                                                <Loader2 className="animate-spin" size={14}/> Carico note…
                                                            </div>
                                                        ) : note.length === 0 ? (
                                                            <div className="text-slate-400 text-sm italic">Nessuna nota operativa per questo turno.</div>
                                                        ) : (
                                                            <ul className="space-y-2">
                                                                {note.map((n: any) => (
                                                                    <li key={n.id} className="bg-white p-3 rounded border border-slate-200 text-sm">
                                                                        <p className="text-slate-800">{n.text}</p>
                                                                        <p className="text-[10px] text-slate-400 mt-1">
                                                                            {n.createdAt ? new Date(n.createdAt).toLocaleString('it-IT') : ''}
                                                                            {(n.lat != null && n.lon != null) && ` · GPS ${n.lat.toFixed(4)}, ${n.lon.toFixed(4)}`}
                                                                        </p>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {r.rep?.testoRelazione && (
                                                            <div className="mt-3 pt-3 border-t border-slate-200">
                                                                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Relazione fine turno (facoltativa)</div>
                                                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.rep.testoRelazione}</p>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200">
                                <tr>
                                    <td/>
                                    <td colSpan={2} className="p-4 text-right text-xs font-bold text-slate-500 uppercase tracking-widest">Totali</td>
                                    <td className="p-4 text-right font-mono font-bold text-slate-900">{totali.orePianificate.toFixed(2)}h</td>
                                    <td/>
                                    <td className="p-4 text-right">
                                        <div className="font-mono text-slate-500">{totali.orePagate.toFixed(2)}h</div>
                                        {totali.oreExtraApprovate > 0 && (
                                            <div className="text-[10px] text-slate-400 font-mono">
                                                incl. {totali.oreExtraApprovate.toFixed(2)} extra
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-right font-black text-slate-900">{fmtEur(totali.ricavoCliente)}</td>
                                    <td className="p-4 text-right">
                                        <div className="font-mono text-slate-500">{fmtEur(totali.costoCollab)}</div>
                                        {totali.rimborsoKm > 0 && (
                                            <div className="text-[10px] text-slate-400 font-mono">
                                                incl. {totali.kmDaRimborsare} km auto propria · {fmtEur(totali.rimborsoKm)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-right font-mono text-slate-600">{fmtEur(totali.spese)}</td>
                                    <td/>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            {/* Movimenti, preventivi collegati e proforma emesse */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Movimentazione</h3>
                    <button onClick={openNuovoMov} className="text-[10px] font-bold bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm">
                        <PlusCircle size={14}/> Nuovo Record
                    </button>
                </div>

                {preventivoList.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
                            <FileText size={13} className="text-indigo-500"/>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Preventivi correlati</span>
                            <span className="text-[10px] font-bold text-slate-400">({preventivoList.length})</span>
                        </div>
                        <ul className="divide-y divide-slate-100">
                            {preventivoList.map((p: any) => (
                                <li key={p.id}
                                    className="px-4 py-2 hover:bg-indigo-50/50 flex items-center justify-between gap-3 transition-colors">
                                    <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)}>
                                        <span className="text-xs font-bold text-indigo-700 shrink-0">#{p.numeroProgressivo || p.id}</span>
                                        <span className="text-[10px] text-slate-400 shrink-0">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                        {p.stato && (
                                            <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">{p.stato}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                        <button
                                            onClick={e => { e.stopPropagation(); unlinkPreventivo(p.id); }}
                                            title="Scollega dal servizio"
                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                        >
                                            <X size={13}/>
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {proformaList.length > 0 && (
                    <div className="bg-white border border-blue-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-blue-100 flex items-center gap-2 bg-blue-50/60">
                            <Receipt size={13} className="text-blue-600"/>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Proforma emesse</span>
                            <span className="text-[10px] font-bold text-blue-400">({proformaList.length})</span>
                            {loadingPrev && <Loader2 size={12} className="animate-spin text-blue-400 ml-auto"/>}
                        </div>
                        <ul className="divide-y divide-blue-100">
                            {proformaList.map((p: any) => (
                                <li key={p.id} className="px-4 py-2.5 hover:bg-blue-50/30 flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)}>
                                        <span className="text-xs font-bold text-blue-700 shrink-0">#{p.numeroProgressivo || p.id}</span>
                                        <span className="text-[10px] text-slate-400 shrink-0">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                        {p.pdfUrl && (
                                            <a href={p.pdfUrl} target="_blank" rel="noreferrer"
                                               onClick={e => e.stopPropagation()}
                                               className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1">
                                                <Download size={11}/> PDF
                                            </a>
                                        )}
                                        {p.fatturaElettronicaEmessa && (
                                            <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">FATT. ELETTR.</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                        <button
                                            onClick={e => { e.stopPropagation(); unlinkPreventivo(p.id); }}
                                            title="Scollega dal servizio (la proforma torna standalone)"
                                            className="p-1 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                        >
                                            <X size={13}/>
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); deleteProforma(p.id); }}
                                            title="Elimina questa proforma"
                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                        >
                                            <Trash2 size={13}/>
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Pendenti del cliente: "Collega" li aggancia al servizio e aggiorna entrambe le liste. */}
                {preventiviPendenti.length > 0 && (
                    <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-amber-100 flex items-center gap-2 bg-amber-50/60">
                            <PlusCircle size={13} className="text-amber-600"/>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Pendenti del cliente (non collegati)</span>
                            <span className="text-[10px] font-bold text-amber-400">({preventiviPendenti.length})</span>
                        </div>
                        <ul className="divide-y divide-amber-100">
                            {preventiviPendenti.map((p: any) => {
                                const isProforma = p.tipoDocumento === 'PROFORMA';
                                return (
                                    <li key={p.id} className="px-4 py-2 hover:bg-amber-50/40 flex items-center justify-between gap-3 transition-colors">
                                        <div className="flex flex-col min-w-0 gap-0.5 cursor-pointer" onClick={() => navigate(`/preventivi/${p.id}`)}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs font-bold text-amber-800">#{p.numeroProgressivo || p.id}</span>
                                                <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                    isProforma ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                                                }`}>{isProforma ? 'PROFORMA' : 'PREVENTIVO'}</span>
                                            </div>
                                            <span className="text-[10px] text-slate-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('it-IT') : ''}</span>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <span className="text-xs font-black text-slate-700">€ {Number(p.totaleIvato ?? p.totale ?? p.imponibile ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                            <button
                                                onClick={() => linkPreventivo(p.id)}
                                                title="Collega al servizio corrente"
                                                className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-600 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors"
                                            >
                                                <PlusCircle size={11}/> Collega
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    {movimenti.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-slate-400 space-y-2 text-center">
                            <Wallet size={32} className="opacity-50"/>
                            <span className="text-sm font-medium">Nessuna transazione registrata.</span>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {movimentiOrdinati.map(mov => {
                                const ent = isEntrata(mov);
                                return (
                                    <div key={mov.id} className="p-3 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={`p-2 rounded-full border shrink-0 ${ent ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                                                {ent ? <ArrowDownRight size={14}/> : <ArrowUpRight size={14}/>}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-slate-800 truncate">{mov.descrizione || '—'}</div>
                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{new Date(mov.data || Date.now()).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            {ent && (
                                                <label className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider cursor-pointer transition-colors border ${
                                                    mov.fatturato ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                                                }`} title={mov.fatturato ? 'Fattura emessa — clicca per togliere' : 'Spunta se hai emesso la fattura per questo movimento'}>
                                                    <input type="checkbox" className="w-3 h-3 rounded cursor-pointer accent-blue-600"
                                                           checked={!!mov.fatturato} onChange={() => toggleFatturato(mov.id)}/>
                                                    Fatturato
                                                </label>
                                            )}
                                            <div className={`text-sm font-black ${ent ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                {ent ? '+' : '−'} €{(Number(mov.importo) || 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </div>
                                            <button onClick={() => eliminaMovimento(mov.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-500 p-1 rounded" title="Elimina">
                                                <Trash2 size={13}/>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modale rettifiche al compenso */}
            {aggModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                     onClick={() => !savingAgg && setAggModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Rettifiche al Compenso</h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    Base: <strong>{fmtEur(baseConsuntivo)}</strong> (consuntivo servizio)
                                </p>
                            </div>
                            <button onClick={() => setAggModalOpen(false)} disabled={savingAgg}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="p-5 space-y-2.5 max-h-[40vh] overflow-y-auto">
                            {aggEdit.length === 0 && (
                                <div className="text-center py-6 text-slate-400 text-sm">Nessuna rettifica. Aggiungila con i pulsanti in basso.</div>
                            )}
                            {aggEdit.map((a, i) => {
                                const mode: IvaMode = (a.ivaMode as IvaMode) || DEFAULT_RETTIFICA_MODE;
                                const s = splitRettifica(Number(a.importo) || 0, mode);
                                const setMode = (m: IvaMode) => { const upd = [...aggEdit]; upd[i] = { ...a, ivaMode: m }; setAggEdit(upd); };
                                return (
                                <div key={a.id} className="space-y-1 border-b border-slate-50 pb-2">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => {
                                                    const upd = [...aggEdit];
                                                    upd[i] = { ...a, importo: -a.importo };
                                                    setAggEdit(upd);
                                                }}
                                                className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-black border transition-colors ${
                                                    Number(a.importo) >= 0
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                        : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                                }`}>
                                            {Number(a.importo) >= 0 ? '+ Extra' : '− Sconto'}
                                        </button>
                                        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden shrink-0">
                                            <span className="px-2 text-xs text-slate-400 bg-slate-50 border-r border-slate-200 py-1.5">€</span>
                                            <NumberInput min="0" step="0.01" value={Math.abs(Number(a.importo)) || ''}
                                                   onChange={e => {
                                                       const v = parseDecimal(e.target.value);
                                                       const upd = [...aggEdit];
                                                       upd[i] = { ...a, importo: Number(a.importo) < 0 ? -v : v };
                                                       setAggEdit(upd);
                                                   }}
                                                   className="w-24 px-2 py-1.5 text-sm outline-none" placeholder="0.00"/>
                                        </div>
                                        <input type="text" value={a.descrizione}
                                               onChange={e => {
                                                   const upd = [...aggEdit];
                                                   upd[i] = { ...a, descrizione: e.target.value };
                                                   setAggEdit(upd);
                                               }}
                                               placeholder="Descrizione..."
                                               className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100"/>
                                        <button onClick={() => setAggEdit(aggEdit.filter((_, j) => j !== i))}
                                                className="shrink-0 p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                                            <X size={14}/>
                                        </button>
                                    </div>
                                    {/* Modalità IVA e anteprima della divisione. */}
                                    <div className="flex items-center gap-1.5 flex-wrap pl-1">
                                        {(['LORDO', 'NETTO', 'SOLO_IVA', 'SOLO_IMPONIBILE'] as IvaMode[]).map(m => (
                                            <button key={m} onClick={() => setMode(m)}
                                                className={`text-[10px] font-black px-1.5 py-0.5 rounded border transition-colors ${
                                                    mode === m ? 'bg-blue-100 text-blue-700 border-blue-200'
                                                        : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                                                {IVA_MODE_LABEL[m]}
                                            </button>
                                        ))}
                                        <span className="text-[10px] text-slate-400 ml-1">
                                            imp {s.imp >= 0 ? '+' : '−'} €{Math.abs(s.imp).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            {' · '}IVA {s.iva >= 0 ? '+' : '−'} €{Math.abs(s.iva).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        <div className="px-5 pb-3 flex gap-2">
                            <button onClick={() => setAggEdit([...aggEdit, { id: `${Date.now()}-${Math.random()}`, descrizione: '', importo: 0, ivaMode: DEFAULT_RETTIFICA_MODE }])}
                                    className="flex-1 border-2 border-dashed border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors">
                                <PlusCircle size={13}/> Aggiungi extra
                            </button>
                            <button onClick={() => setAggEdit([...aggEdit, { id: `${Date.now()}-${Math.random()}`, descrizione: '', importo: -0.01, ivaMode: DEFAULT_RETTIFICA_MODE }])}
                                    className="flex-1 border-2 border-dashed border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors">
                                <PlusCircle size={13}/> Aggiungi sconto
                            </button>
                        </div>
                        {aggEdit.length > 0 && (
                            <div className="mx-5 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-500">
                                    Base €{baseConsuntivo.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    {' '}{aggEdit.reduce((s, a) => s + Number(a.importo || 0), 0) >= 0 ? '+' : ''}€{aggEdit.reduce((s, a) => s + Number(a.importo || 0), 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                                <span className="text-sm font-black text-slate-900 shrink-0">
                                    = €{(baseConsuntivo + aggEdit.reduce((s, a) => s + Number(a.importo || 0), 0)).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                            </div>
                        )}
                        <div className="p-5 pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                            {aggEdit.length > 0 ? (
                                <button onClick={() => setAggEdit([])} disabled={savingAgg}
                                        className="text-xs text-slate-400 hover:text-rose-500 flex items-center gap-1 disabled:opacity-50">
                                    <Trash2 size={12}/> Rimuovi tutte
                                </button>
                            ) : <div/>}
                            <div className="flex items-center gap-2">
                                <button onClick={() => setAggModalOpen(false)} disabled={savingAgg}
                                        className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50">
                                    Annulla
                                </button>
                                <button onClick={handleSaveAgg} disabled={savingAgg}
                                        className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
                                    {savingAgg ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                                    Salva rettifiche
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale nuovo movimento */}
            {movModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                     onClick={() => !savingMov && setMovModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Nuovo Incasso</h3>
                                <p className="text-xs text-slate-500 mt-1">Registra un pagamento ricevuto dal cliente.</p>
                            </div>
                            <button onClick={() => setMovModalOpen(false)} disabled={savingMov}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Data</label>
                                <input type="date" value={newMov.data} onChange={e => setNewMov({...newMov, data: e.target.value})}
                                       className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"/>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Descrizione</label>
                                <input type="text" value={newMov.descrizione} onChange={e => setNewMov({...newMov, descrizione: e.target.value})}
                                       placeholder="Es. Acconto cliente bonifico" className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"/>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Importo €</label>
                                <input type="text" value={newMov.importo} onChange={e => setNewMov({...newMov, importo: e.target.value})}
                                       placeholder="0.00" className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm font-mono text-right"/>
                            </div>
                            {/* Modalità IVA dell'incasso. */}
                            <div className="md:col-span-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Modalità IVA</label>
                                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                    {(['LORDO', 'NETTO', 'SOLO_IVA'] as IvaMode[]).map(m => (
                                        <button key={m} type="button" onClick={() => setNewMov({...newMov, ivaMode: m})}
                                            className={`text-xs font-bold px-2.5 py-1 rounded-lg border transition-colors ${
                                                newMov.ivaMode === m ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}>
                                            {IVA_MODE_LABEL[m]}
                                        </button>
                                    ))}
                                    {parseDecimal(newMov.importo) > 0 && (() => {
                                        const s = splitIncasso(parseDecimal(newMov.importo), newMov.ivaMode);
                                        return <span className="text-[11px] text-slate-500 ml-1">imp {fmtEur(s.imp)} · IVA {fmtEur(s.iva)}</span>;
                                    })()}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
                            <button onClick={() => setMovModalOpen(false)} disabled={savingMov}
                                    className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50">
                                Annulla
                            </button>
                            <button onClick={handleSaveNuovoMov} disabled={savingMov}
                                    className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
                                {savingMov ? <Loader2 size={14} className="animate-spin"/> : <PlusCircle size={14}/>}
                                Aggiungi
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale anteprima proforma */}
            {proformaModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto animate-in fade-in"
                     onClick={() => !generatingProforma && setProformaModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl lg:max-w-4xl w-full overflow-hidden animate-in slide-in-from-bottom-4" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Anteprima Fattura Proforma</h3>
                                <p className="text-xs text-slate-500 mt-1">Verifica e modifica le righe prima di generare il PDF.</p>
                            </div>
                            <button onClick={() => !generatingProforma && setProformaModalOpen(false)} disabled={generatingProforma}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500 font-bold tracking-wider">
                                        <tr>
                                            <th className="text-left p-3">Descrizione</th>
                                            <th className="text-right p-3 w-20">Qtà</th>
                                            <th className="text-right p-3 w-28">Tariffa</th>
                                            <th className="text-right p-3 w-32">Importo €</th>
                                            <th className="w-10 p-3"/>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {proformaRows.map(r => (
                                            <tr key={r.id} className="hover:bg-slate-50">
                                                <td className={`p-3 text-sm ${r.inTotal === false ? 'text-slate-400 italic' : 'text-slate-800'}`}>{r.descrizione}</td>
                                                <td className="p-3 text-right text-slate-500 text-xs">{r.quantita}</td>
                                                <td className="p-3 text-right text-slate-500 text-xs">{r.tariffa}</td>
                                                <td className="p-3 text-right">
                                                    {r.inTotal === false ? (
                                                        // Riga informativa (Solo IVA): la sua IVA è già nel totale.
                                                        <span className="font-mono text-sm text-slate-400 italic" title="Fuori dall'imponibile: l'importo è già compreso nell'IVA">{fmtEur(Number(r.importo) || 0)}</span>
                                                    ) : (
                                                        <input type="text" value={String(r.importo)}
                                                               onChange={e => updateProformaRowImporto(r.id, e.target.value)}
                                                               className="w-28 p-1 border border-slate-200 rounded text-sm font-mono text-right"/>
                                                    )}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <button onClick={() => removeProformaRow(r.id)} className="text-slate-300 hover:text-rose-500 p-1">
                                                        <Trash2 size={14}/>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                                <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-wider">Aggiungi voce</p>
                                <div className="grid grid-cols-12 gap-2 items-end">
                                    <div className="col-span-5">
                                        <input type="text" placeholder="Descrizione" value={newRowDraft.descrizione}
                                               onChange={e => setNewRowDraft({...newRowDraft, descrizione: e.target.value})}
                                               className="w-full p-2 border border-slate-200 rounded-lg text-sm"/>
                                    </div>
                                    <div className="col-span-2">
                                        <input type="text" placeholder="Qta" value={newRowDraft.quantita}
                                               onChange={e => {
                                                   const v = e.target.value;
                                                   setNewRowDraft(prev => {
                                                       const q = parseDecimal(v);
                                                       const t = parseDecimal(prev.tariffa);
                                                       return {
                                                           ...prev,
                                                           quantita: v,
                                                           importo: q > 0 && t > 0 ? (Math.round(q * t * 100) / 100).toFixed(2) : prev.importo,
                                                       };
                                                   });
                                               }}
                                               className="w-full p-2 border border-slate-200 rounded-lg text-sm text-right"/>
                                    </div>
                                    <div className="col-span-2">
                                        <input type="text" placeholder="Tariffa" value={newRowDraft.tariffa}
                                               onChange={e => {
                                                   const v = e.target.value;
                                                   setNewRowDraft(prev => {
                                                       const q = parseDecimal(prev.quantita);
                                                       const t = parseDecimal(v);
                                                       return {
                                                           ...prev,
                                                           tariffa: v,
                                                           importo: q > 0 && t > 0 ? (Math.round(q * t * 100) / 100).toFixed(2) : prev.importo,
                                                       };
                                                   });
                                               }}
                                               className="w-full p-2 border border-slate-200 rounded-lg text-sm text-right"/>
                                    </div>
                                    <div className="col-span-2">
                                        <input type="text" placeholder="Importo €" value={newRowDraft.importo}
                                               onChange={e => setNewRowDraft({...newRowDraft, importo: e.target.value})}
                                               className="w-full p-2 border border-slate-200 rounded-lg text-sm font-mono text-right"/>
                                    </div>
                                    <div className="col-span-1">
                                        <button onClick={addProformaRow}
                                                className="w-full bg-slate-900 hover:bg-slate-800 text-white p-2 rounded-lg">
                                            <PlusCircle size={14}/>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-3 text-sm">
                                <div className="text-right">
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Imponibile</p>
                                    <p className="font-mono text-slate-800 font-bold">{fmtEur(proformaImponibile)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">IVA {aliquotaIva}%</p>
                                    <p className="font-mono text-slate-800">{fmtEur(proformaIva)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Totale</p>
                                    <p className="font-mono font-black text-slate-900 text-lg">{fmtEur(proformaTotale)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
                            <button onClick={() => !generatingProforma && setProformaModalOpen(false)} disabled={generatingProforma}
                                    className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50">
                                Annulla
                            </button>
                            <button onClick={handleGeneraProforma} disabled={generatingProforma || proformaRows.length === 0}
                                    className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
                                {generatingProforma ? <Loader2 size={14} className="animate-spin"/> : <FileDown size={14}/>}
                                Genera & Archivia
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabContabilitaServizio;
